import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import ChatPanel from './components/ChatPanel';
import TipModal from './components/TipModal';
import ReviewForm from './components/ReviewForm';
import LeafletLiveTracker from '../../components/feature/LeafletLiveTracker';

interface Job {
  id: string;
  service_category: string;
  service_subcategory: string;
  customer_name: string;
  customer_address: string;
  customer_postcode: string;
  scheduled_date: string | null;
  is_instant: boolean;
  status: string;
  provider_on_route: boolean;
  provider_arrived: boolean;
  job_started_at: string | null;
  job_completed_at: string | null;
  provider_id: string | null;
  customer_id: string;
  provider_latitude: number | null;
  provider_longitude: number | null;
  customer_latitude: number;
  customer_longitude: number;
  total_amount: number | null;
  payment_status: string;
  invoice_number: string | null;
  tip_amount: number | null;
}

interface Provider {
  id: string;
  business_name: string;
  phone: string;
  profile_image: string | null;
}

export default function JobTrackingPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState<Job | null>(null);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [userRole, setUserRole] = useState<'client' | 'provider' | null>(null);
  const [addingToFavorites, setAddingToFavorites] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [success, setSuccess] = useState('');
  const [updating, setUpdating] = useState(false);

  // Check for tip success/cancelled
  const tipSuccess = searchParams.get('tip_success');
  const tipCancelled = searchParams.get('tip_cancelled');

  useEffect(() => {
    // Set document title and meta tags
    document.title = 'Track Your Job | Live Service Tracking - Taskbudy UK';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Track your service job in real-time with Taskbudy. View provider location, chat with your provider, upload photos, and manage your booking with live updates.');
    }
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        setError('Job ID is missing. Please check your link and try again.');
        setLoading(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setError('Please sign in to view job details.');
          setLoading(false);
          setTimeout(() => {
            window.REACT_APP_NAVIGATE('/signin');
          }, 2000);
          return;
        }

        const { data, error } = await supabase
          .from('jobs')
          .select(`
            *,
            client:users!jobs_client_id_fkey(name, email, phone),
            provider:users!jobs_provider_id_fkey(name, email, phone),
            service_category:service_categories(name),
            service_subcategory:service_subcategories(name)
          `)
          .eq('id', jobId)
          .single();

        if (error) {
          console.error('Error fetching job:', error);
          if (error.code === 'PGRST116') {
            setError('Job not found. It may have been cancelled or deleted.');
          } else {
            setError('Unable to load job details. Please refresh the page.');
          }
          setLoading(false);
          return;
        }

        if (!data) {
          setError('Job not found. Please check your job ID.');
          setLoading(false);
          return;
        }

        // Verify user has access to this job
        if (data.client_id !== user.id && data.provider_id !== user.id) {
          setError('You do not have permission to view this job.');
          setLoading(false);
          return;
        }

        setJob(data);
        setUserRole(data.client_id === user.id ? 'client' : 'provider');
        setLoading(false);
      } catch (err) {
        console.error('Job fetch error:', err);
        setError('Connection error. Please check your internet and try again.');
        setLoading(false);
      }
    };

    fetchJobDetails();

    // Subscribe to job updates
    const subscription = supabase
      .channel(`job-${jobId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jobs',
          filter: `id=eq.${jobId}`,
        },
        (payload) => {
          if (payload.new) {
            setJob((prev) => ({ ...prev, ...payload.new }));
            
            // Show status update notification
            if (payload.new.status !== payload.old?.status) {
              const statusMessages = {
                accepted: 'Provider has accepted your job!',
                in_progress: 'Provider has started working on your job.',
                completed: 'Job has been completed!',
                cancelled: 'Job has been cancelled.',
              };
              
              const message = statusMessages[payload.new.status as keyof typeof statusMessages];
              if (message) {
                setSuccess(message);
                setTimeout(() => setSuccess(''), 5000);
              }
            }
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [jobId]);

  useEffect(() => {
    if (job?.provider_id) {
      checkIfFavorite();
    }
  }, [job?.provider_id]);

  const loadCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const loadJobDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (error) throw error;

      setJob(data);
      if (data.provider_id) {
        await loadProviderDetails(data.provider_id);
      }
    } catch (err: any) {
      console.error('Error loading job:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const loadProviderDetails = async (providerId: string) => {
    try {
      const { data, error } = await supabase
        .from('provider_profiles')
        .select('id, business_name, phone, profile_image')
        .eq('id', providerId)
        .single();

      if (error) throw error;
      setProvider(data);
    } catch (err) {
      console.error('Error loading provider:', err);
    }
  };

  const checkIfFavorite = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data } = await supabase
        .from('favorite_providers')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider_id', job?.provider_id)
        .single();

      setIsFavorite(!!data);
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const addToFavorites = async () => {
    try {
      setAddingToFavorites(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/add-favorite-provider`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            provider_id: job?.provider_id,
            notes: `Great ${job?.service_type} service`,
          }),
        }
      );

      if (response.ok) {
        setIsFavorite(true);
        alert('Provider added to your favorites!');
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
      alert('Failed to add to favorites');
    } finally {
      setAddingToFavorites(false);
    }
  };

  const handlePayment = async () => {
    if (!job) return;

    setProcessingPayment(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-job-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            jobId: job.id,
            amount: job.total_amount
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create payment');
      }

      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;

    } catch (err: any) {
      console.error('Payment error:', err);
      alert('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!job) return;

    setUpdating(true);
    setError('');

    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: newStatus })
        .eq('id', job.id);

      if (error) {
        console.error('Status update error:', error);
        setError('Unable to update job status. Please try again.');
        setUpdating(false);
        return;
      }

      setSuccess('Job status updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setUpdating(false);
    } catch (err) {
      console.error('Status update error:', err);
      setError('Connection error. Please check your internet and try again.');
      setUpdating(false);
    }
  };

  const handleStartNavigation = () => {
    if (!job) return;

    const lat = job.customer_latitude || job.latitude;
    const lng = job.customer_longitude || job.longitude;

    if (!lat || !lng) {
      alert('Job location not available');
      return;
    }

    // Detect device type
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    let navigationUrl = '';

    if (isIOS) {
      // Apple Maps deep link
      navigationUrl = `http://maps.apple.com/?daddr=${lat},${lng}`;
    } else if (isAndroid) {
      // Google Maps deep link for Android
      navigationUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    } else {
      // Desktop/fallback - Google Maps web
      navigationUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    }

    window.open(navigationUrl, '_blank');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Searching for Provider';
      case 'accepted': return 'Provider Assigned';
      case 'in_progress': return 'Job in Progress';
      case 'completed': return 'Job Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#0B3C5D] animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error || 'Job not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isCustomer = currentUserId === job.customer_id;
  const isChatEnabled = job.status === 'accepted' || job.status === 'in_progress';

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white overflow-x-hidden">
        {/* Navigation */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                  alt="TaskBudy" 
                  className="h-8 md:h-10"
                />
              </Link>
              <button
                onClick={() => navigate('/')}
                className="px-3 sm:px-4 py-2 text-sm text-gray-700 hover:text-[#0B3C5D] transition-colors whitespace-nowrap cursor-pointer font-medium"
              >
                Back to Home
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          {/* Tip Success/Cancelled Messages */}
          {tipSuccess && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <i className="ri-check-line text-3xl sm:text-4xl text-green-600 flex-shrink-0"></i>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-green-900">Tip Sent Successfully! 🎉</h3>
                <p className="text-sm sm:text-base text-green-700">Your provider will receive 100% of your generous tip. Thank you!</p>
              </div>
            </div>
          )}
          {tipCancelled && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <i className="ri-information-line text-3xl sm:text-4xl text-amber-600 flex-shrink-0"></i>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-amber-900">Tip Cancelled</h3>
                <p className="text-sm sm:text-base text-amber-700">You can still leave a tip anytime from this page.</p>
              </div>
            </div>
          )}

          {/* Status Header */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-8 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Job Tracking</h1>
                <p className="text-sm sm:text-base text-gray-600">Job ID: {job.id.slice(0, 8)}</p>
              </div>
              <span className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold self-start sm:self-auto ${getStatusColor(job.status)}`}>
                {getStatusText(job.status)}
              </span>
            </div>

            {/* Progress Timeline */}
            <div className="relative overflow-x-auto pb-4">
              <div className="flex items-center justify-between min-w-[600px] sm:min-w-0">
                {[
                  { key: 'pending', label: 'Request Submitted', icon: 'ri-file-list-3-line' },
                  { key: 'accepted', label: 'Provider Assigned', icon: 'ri-user-star-line' },
                  { key: 'in_progress', label: 'Job in Progress', icon: 'ri-tools-line' },
                  { key: 'completed', label: 'Completed', icon: 'ri-check-line' }
                ].map((step, index) => {
                  const isActive = 
                    (step.key === 'pending') ||
                    (step.key === 'accepted' && ['accepted', 'in_progress', 'completed'].includes(job.status)) ||
                    (step.key === 'in_progress' && ['in_progress', 'completed'].includes(job.status)) ||
                    (step.key === 'completed' && job.status === 'completed');

                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-[#0B3C5D] text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        <i className={`${step.icon} text-lg sm:text-xl`}></i>
                      </div>
                      <p className="text-[10px] sm:text-xs font-medium text-center mt-2 px-1">{step.label}</p>
                      {index < 3 && (
                        <div className={`absolute top-5 sm:top-6 h-1 ${
                          isActive ? 'bg-[#0B3C5D]' : 'bg-gray-200'
                        }`} style={{
                          left: `${(index + 1) * 25}%`,
                          width: '25%',
                          transform: 'translateX(-50%)'
                        }}></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Job Details */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Job Details</h2>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Service</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{job.service_subcategory}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Location</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{job.customer_address}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{job.customer_postcode}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Scheduled</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">
                    {job.is_instant ? 'As Soon As Possible' : new Date(job.scheduled_date!).toLocaleString()}
                  </p>
                </div>
                {job.job_started_at && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Started At</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">
                      {new Date(job.job_started_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {job.job_completed_at && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Completed At</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">
                      {new Date(job.job_completed_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Provider Details */}
            {provider ? (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Provider</h2>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#0B3C5D] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {provider.business_name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{provider.business_name}</h3>
                    <p className="text-gray-600">{provider.phone}</p>
                  </div>
                </div>

                {/* Start Navigation Button - Show after acceptance */}
                {job.status !== 'pending' && job.status !== 'completed' && job.status !== 'cancelled' && !isCustomer && (
                  <button
                    onClick={handleStartNavigation}
                    className="w-full mb-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-navigation-line text-xl"></i>
                    Start Navigation
                  </button>
                )}

                {/* Provider Status */}
                {job.provider_on_route && !job.provider_arrived && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-car-line text-2xl text-blue-600"></i>
                      <div>
                        <p className="font-semibold text-blue-900">Provider is on the way!</p>
                        <p className="text-sm text-blue-700">Track their location below</p>
                      </div>
                    </div>
                  </div>
                )}

                {job.provider_arrived && !job.job_started_at && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-map-pin-line text-2xl text-green-600"></i>
                      <div>
                        <p className="font-semibold text-green-900">Provider has arrived!</p>
                        <p className="text-sm text-green-700">They'll start the job shortly</p>
                      </div>
                    </div>
                  </div>
                )}

                {job.job_started_at && !job.job_completed_at && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-tools-line text-2xl text-purple-600"></i>
                      <div>
                        <p className="font-semibold text-purple-900">Job in progress</p>
                        <p className="text-sm text-purple-700">Started {new Date(job.job_started_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Finding Provider</h2>
                <div className="text-center py-12">
                  <i className="ri-search-line text-5xl text-[#FF6A00] mb-4"></i>
                  <p className="text-gray-600 mb-2">We're searching for available providers in your area</p>
                  <p className="text-sm text-gray-500">This usually takes just a few minutes</p>
                </div>
              </div>
            )}
          </div>

          {/* Live Location Tracker - Leaflet/OSM */}
          {provider && job.provider_id && job.customer_latitude && job.customer_longitude && (
            <div className="mt-6">
              <LeafletLiveTracker
                jobId={job.id}
                providerId={job.provider_id}
                customerLatitude={job.customer_latitude}
                customerLongitude={job.customer_longitude}
                customerAddress={job.customer_address}
                isProvider={!isCustomer}
                jobStatus={job.status}
              />
            </div>
          )}

          {/* Chat Panel - Only show if provider is assigned AND job is active */}
          {provider && currentUserId && isChatEnabled && (
            <div className="mt-6">
              <ChatPanel 
                jobId={job.id} 
                userId={currentUserId} 
                userType={isCustomer ? 'customer' : 'provider'}
                jobStatus={job.status}
              />
            </div>
          )}

          {/* Chat Disabled Message */}
          {provider && currentUserId && !isChatEnabled && (
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
              <i className="ri-chat-off-line text-3xl text-gray-400 mb-2"></i>
              <p className="text-gray-600 font-medium">Chat is disabled</p>
              <p className="text-sm text-gray-500 mt-1">
                {job.status === 'pending' && 'Chat will be enabled once a provider accepts the job'}
                {job.status === 'completed' && 'Chat is no longer available for completed jobs'}
                {job.status === 'cancelled' && 'Chat is not available for cancelled jobs'}
              </p>
            </div>
          )}

          {/* Invoice Section */}
          {job.status === 'completed' && job.total_amount && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-8 mt-4 sm:mt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Invoice</h2>
              <div className="border-t border-b border-gray-200 py-3 sm:py-4 space-y-2 sm:space-y-3">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Invoice Number</span>
                  <span className="font-semibold">{job.invoice_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-xl sm:text-2xl font-bold">
                  <span>Total Amount</span>
                  <span className="text-[#0B3C5D]">£{job.total_amount.toFixed(2)}</span>
                </div>
                {job.tip_amount && job.tip_amount > 0 && (
                  <div className="flex justify-between text-base sm:text-lg text-amber-600">
                    <span className="flex items-center gap-2">
                      <i className="ri-gift-line"></i>
                      Tip Added
                    </span>
                    <span className="font-semibold">£{job.tip_amount.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {job.payment_status === 'pending' ? (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="w-full mt-4 sm:mt-6 py-3 sm:py-4 bg-gradient-to-r from-[#FF6A00] to-[#FF8533] text-white rounded-lg hover:from-[#E55F00] hover:to-[#E57A2E] transition-all duration-300 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Pay Now
                </button>
              ) : (
                <>
                  <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <i className="ri-check-line text-xl sm:text-2xl text-green-600 mb-2"></i>
                    <p className="font-semibold text-green-900 text-sm sm:text-base">Payment Completed</p>
                    <p className="text-xs sm:text-sm text-green-700">Thank you for using Taskbudy!</p>
                  </div>

                  {/* Tip and Review Buttons - Only for customers after payment */}
                  {isCustomer && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <button
                        onClick={() => setShowTipModal(true)}
                        className="py-2.5 sm:py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <i className="ri-gift-line text-lg sm:text-xl"></i>
                        Leave a Tip
                      </button>
                      <button
                        onClick={() => setShowReviewForm(true)}
                        className="py-2.5 sm:py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <i className="ri-star-line text-lg sm:text-xl"></i>
                        Leave a Review
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Review Section - Show after completion */}
          {job?.status === 'completed' && !job?.review_submitted && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Rate Your Experience</h2>
                  <p className="text-gray-600">Help others by sharing your feedback</p>
                </div>
                {!isFavorite && (
                  <button
                    onClick={addToFavorites}
                    disabled={addingToFavorites}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium whitespace-nowrap disabled:opacity-50"
                  >
                    {addingToFavorites ? (
                      <>
                        <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <i className="ri-heart-line text-lg"></i>
                        Add to Favorites
                      </>
                    )}
                  </button>
                )}
                {isFavorite && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-lg font-medium">
                    <i className="ri-heart-fill text-lg"></i>
                    In Your Favorites
                  </div>
                )}
              </div>
              <ReviewForm jobId={job.id} providerId={job.provider_id} />
            </div>
          )}
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setShowPaymentModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Complete Payment</h3>
              <p className="text-gray-600 mb-6">
                You'll be redirected to our secure payment page to complete your payment of £{job.total_amount?.toFixed(2)}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePayment}
                  disabled={processingPayment}
                  className="flex-1 px-6 py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {processingPayment ? 'Processing...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tip Modal */}
        {showTipModal && provider && (
          <TipModal
            jobId={job.id}
            providerName={provider.business_name}
            onClose={() => setShowTipModal(false)}
          />
        )}

        {/* Review Form Modal */}
        {showReviewForm && provider && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="my-8">
              <ReviewForm
                jobId={job.id}
                providerId={provider.id}
                onClose={() => setShowReviewForm(false)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
